import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Compass, Target, Clapperboard, Percent, Unlock, Wallet, BadgeEuro, UserCheck } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { VimeoPlayer } from "../components/VimeoPlayer";
import { LeadFormModal } from "../components/LeadFormModal";
import { Reveal, KineticLines } from "../components/Reveal";
import { track, trackOncePerSession } from "../lib/tracking";

const SERVICE_PARTS = [
  {
    icon: Compass,
    title: "2 ore di consulenza direzionale",
    text: "Parleremo insieme a fondo di tutta la parte direzionale: i soldi spendibili per acquisire ogni cliente e la capacità produttiva inutilizzata del tuo salone.",
  },
  {
    icon: Target,
    title: "1 ora e mezza di consulenza strategica e tattica",
    text: "Definiremo il Piano: quali clienti vogliamo prendere, con quale messaggio e con quale percorso trasmettere le informazioni alla cliente.",
  },
  {
    icon: Clapperboard,
    title: "Messa a terra completa",
    text: "Realizziamo tutto ciò che è stato stabilito. Se ci saranno video da creare, valuteremo se realizzarli con l'AI, con il volto di un collaboratore, o se lasciare a te il compito di registrarti recitando il nostro copione.",
  },
];

const CONDITIONS = [
  {
    icon: Percent,
    title: "Paghi solo a % sull'aumento di fatturato",
    text: "Tra il 5% e il 15% dell'aumento di fatturato rispetto allo stesso mese dell'anno precedente. Esempio: se a ottobre 2026 fatturi 25.000€ e a ottobre 2025 hai fatturato 20.000€, la percentuale si calcola solo sui 5.000€ di differenza.",
  },
  {
    icon: Unlock,
    title: "Contratto non vincolante",
    text: "Puoi rescindere in qualsiasi momento, senza vincoli.",
  },
  {
    icon: Wallet,
    title: "Nessun minimo di spesa pubblicitaria",
    text: "Non c'è un minimo di budget pubblicitario che devi investire al mese.",
  },
  {
    icon: BadgeEuro,
    title: "300€ per partire",
    text: "L'unica cosa che chiediamo per iniziare: copre le consulenze e l'impostazione generale delle campagne da realizzare.",
  },
  {
    icon: UserCheck,
    title: "Un solo referente",
    text: "Il servizio è supervisionato in tutti i suoi passaggi da Pietro Maria Badiali: avrai una sola figura a cui rivolgerti per domande ed esigenze.",
  },
];

export default function Servizio() {
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    trackOncePerSession("pv_vc_servizio", "ViewContent", { content_name: "Pagina Servizio VSL2" });
  }, []);

  const openModal = () => {
    track("CTA2Click", { cta: "Richiedi ulteriori informazioni" });
    setModalOpen(true);
  };

  return (
    <div className="bg-[#F7F2EA] min-h-screen" data-testid="servizio-page">
      <section className="relative bg-[#2B1930] pv-grain overflow-hidden">
        <Navbar dark />
        <div
          className="pv-giant-percent absolute -left-12 bottom-0 text-[34vw] md:text-[22rem] text-[#D83A52]/10 z-0"
          aria-hidden="true"
        >
          %
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 pt-36 md:pt-44 pb-10 text-center">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="text-xs uppercase tracking-[0.35em] font-bold text-[#C8A6A1] mb-8"
          >
            Il servizio, spiegato bene
          </motion.p>

          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.08] text-[#F7F2EA]"
            data-testid="servizio-headline"
          >
            <KineticLines
              lines={[
                <span key="1">
                  Parrucchiere <span className="italic text-[#D83A52]">Vincente</span>
                </span>,
              ]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="mt-6 font-display text-xl sm:text-2xl text-[#F7F2EA]/85 max-w-3xl mx-auto leading-snug"
          >
            L'unica Agenzia di Marketing per Saloni da Parrucchiere che lavora{" "}
            <span className="italic text-[#D83A52] font-bold">a %</span>
          </motion.p>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 pb-8">
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <VimeoPlayer videoId="1217574051" hash="feed03257e" label="VSL2" testId="servizio-vsl-video" />
          </motion.div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 pb-24 text-center">
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            onClick={openModal}
            data-testid="servizio-cta-button"
            className="pv-cta-shine inline-flex items-center gap-3 rounded-full bg-[#D83A52] text-white font-bold text-base sm:text-lg px-10 py-5 hover:brightness-110 hover:-translate-y-0.5 active:scale-[0.98] transition-transform shadow-[0_8px_25px_rgba(216,58,82,0.45)]"
          >
            Richiedi ulteriori informazioni
            <ArrowRight className="h-5 w-5" />
          </motion.button>
        </div>
      </section>

      {/* SERVICE */}
      <section className="max-w-6xl mx-auto px-6 py-28">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.35em] font-bold text-[#C8A6A1] mb-4">Il servizio</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2B1930] max-w-2xl leading-[1.15]">
            Di cosa si compone il <span className="italic text-[#D83A52]">nostro servizio</span>
          </h2>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-3 gap-8">
          {SERVICE_PARTS.map((g, i) => (
            <Reveal key={g.title} delay={i * 0.12}>
              <div className="h-full rounded-2xl bg-white border border-[#C8A6A1]/25 p-8 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(43,25,48,0.12)] hover:border-[#C8A6A1]/60 transition-all duration-300">
                <g.icon className="h-7 w-7 text-[#D83A52] mb-6" />
                <h3 className="font-display text-2xl font-bold text-[#2B1930] mb-3">{g.title}</h3>
                <p className="text-sm leading-relaxed text-[#181518]/70">{g.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CONDITIONS */}
      <section className="bg-white border-y border-[#C8A6A1]/20">
        <div className="max-w-6xl mx-auto px-6 py-28">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.35em] font-bold text-[#C8A6A1] mb-4">Le condizioni, nero su bianco</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2B1930] max-w-2xl leading-[1.15]">
              Un accordo dove <span className="italic text-[#D83A52]">vinci</span> prima tu
            </h2>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-2 gap-x-12 gap-y-10">
            {CONDITIONS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="flex gap-5">
                  <div className="shrink-0 h-12 w-12 rounded-full bg-[#2B1930] flex items-center justify-center">
                    <c.icon className="h-5 w-5 text-[#D83A52]" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-bold text-[#2B1930] mb-1.5">{c.title}</h3>
                    <p className="text-sm leading-relaxed text-[#181518]/70">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2} className="mt-16 text-center">
            <button
              onClick={openModal}
              data-testid="servizio-cta-bottom-button"
              className="pv-cta-shine inline-flex items-center gap-3 rounded-full bg-[#2B1930] text-[#F7F2EA] font-bold text-base px-10 py-5 hover:brightness-125 hover:-translate-y-0.5 active:scale-[0.98] transition-transform shadow-[0_15px_40px_rgba(43,25,48,0.35)]"
            >
              Richiedi ulteriori informazioni
              <ArrowRight className="h-5 w-5 text-[#D83A52]" />
            </button>
          </Reveal>
        </div>
      </section>

      <Footer />
      <LeadFormModal open={modalOpen} onOpenChange={setModalOpen} />

      {/* STICKY MOBILE CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 p-3 bg-[#2B1930]/90 backdrop-blur-lg border-t border-[#C8A6A1]/25">
        <button
          onClick={openModal}
          data-testid="servizio-sticky-cta-button"
          className="w-full rounded-full bg-[#D83A52] text-white font-bold py-4 text-base active:scale-[0.98] transition-transform shadow-[0_8px_25px_rgba(216,58,82,0.45)]"
        >
          Richiedi informazioni
        </button>
      </div>
    </div>
  );
}
